import { Box, Typography } from "@mui/joy";
import { LayoutDashboard, Layers, FileStack, Store, Plus } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { Gate } from "../../shared";
import { PERMISSIONS } from "../../constants/permissions";

interface BottomNavProps {
    onOpenMenu?: () => void;
}

export default function BottomNav({ onOpenMenu }: BottomNavProps) {
    const location = useLocation();

    const items = [
        { name: "Home", href: "/dashboard", icon: LayoutDashboard },
        { name: "Projects", href: "/project", icon: Layers },
        { name: "Reports", href: "/reports/list", icon: FileStack },
        { name: "Suppliers", href: "/suppliers", icon: Store },
    ];

    return (
        <Box
            sx={{
                position: "fixed",
                bottom: "1.5rem",
                left: "50%",
                transform: "translateX(-50%)",
                width: "calc(100% - 2rem)",
                maxWidth: "420px",
                display: { xs: "flex", sm: "none" },
                alignItems: "center",
                gap: 1.5,
                zIndex: 1100,
            }}
        >
            {/* Left Section: Navigation Pill (80% feel) */}
            <Box
                sx={{
                    flex: 1,
                    height: "64px",
                    bgcolor: "background.surface",
                    backdropFilter: "blur(12px)",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "24px",
                    display: "flex",
                    justifyContent: "space-around",
                    alignItems: "center",
                    px: 1,
                }}
            >
                {items.map((item) => {
                    const isActive = location.pathname.startsWith(item.href);
                    const Icon = item.icon;
                    return (
                        <NavLink key={item.href} to={item.href} style={{ textDecoration: "none", color: "inherit" }}>
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                gap={0.5}
                                sx={{
                                    color: isActive ? "primary.plainColor" : "neutral.plainColor",
                                    transition: "all 0.2s ease",
                                    transform: isActive ? "scale(1.1)" : "scale(1)",
                                }}
                            >
                                <Icon size={20} />
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        fontSize: "10px",
                                        fontWeight: isActive ? 700 : 500,
                                        color: "inherit",
                                    }}
                                >
                                    {item.name}
                                </Typography>
                            </Box>
                        </NavLink>
                    );
                })}
            </Box>

            {/* Right Section: Standalone Action FAB (20% feel) */}
            <Gate anyOf={[PERMISSIONS.PROJECT_CREATE]}>
                <NavLink to="/project/new" style={{ textDecoration: "none", color: "inherit" }}>
                    <Box
                        sx={{
                            width: "60px",
                            height: "60px",
                            bgcolor: "primary.softBg",
                            color: "primary.solidBg",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "20px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transition: "all 0.2s ease",
                            "&:active": { transform: "scale(0.9)" },
                            flexShrink: 0,
                        }}
                    >
                        <Plus size={28} />
                    </Box>
                </NavLink>
            </Gate>
        </Box>
    );
}
